import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Edit, Trash2, Search, Users, Copy, Eye,
  Phone, Mail, MapPin, Calendar, FileText, Hash, Printer, Check, Link, Clock,
  Package, Truck, Send, CheckCircle, ExternalLink, Box
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceTypes = [
  "Business Cards", "Flyers", "Sticker", "Brochure", "Menu", "Banner", "Poster", "Otros"
];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming","District of Columbia",
  "Puerto Rico","Guam","U.S. Virgin Islands","American Samoa","Northern Mariana Islands"
];

const ClientsPage = () => {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    street_address: "", unit: "", city: "", state: "", zip_code: "",
    service_types: [], service_date: "", notes: ""
  });

  // Detail dialog
  const [selectedClient, setSelectedClient] = useState(null);

  // Label print
  const [labelClient, setLabelClient] = useState(null);

  // Client orders dialog
  const [ordersClient, setOrdersClient] = useState(null);
  const [clientOrders, setClientOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const ORDER_STATUS_CONFIG = {
    received:    { label: "Recibido",    color: "bg-stone-100 text-stone-700",   icon: Package },
    in_progress: { label: "En Proceso",  color: "bg-amber-100 text-amber-700",   icon: Clock },
    shipped:     { label: "Enviado",     color: "bg-emerald-100 text-emerald-700", icon: Truck },
    delivered:   { label: "Entregado",   color: "bg-blue-100 text-blue-700",     icon: CheckCircle },
    cancelled:   { label: "Cancelado",   color: "bg-red-100 text-red-700",       icon: Trash2 },
  };
  const ACTIVE_STATUSES = ["received", "in_progress", "shipped"];

  const openClientOrders = async (client) => {
    setOrdersClient(client);
    setClientOrders([]);
    setOrdersLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } });
      const allOrders = res.data.orders || res.data || [];
      // Match by client_id primarily, fallback to email match
      const filtered = allOrders.filter(
        (o) => o.client_id === client.id ||
               (client.email && o.client_email && o.client_email.toLowerCase() === client.email.toLowerCase())
      );
      setClientOrders(filtered);
    } catch {
      toast.error("Error al cargar pedidos del cliente");
    } finally {
      setOrdersLoading(false);
    }
  };

  const handlePrintLabel = (client) => {
    setLabelClient(client);
    setTimeout(() => {
      const el = document.getElementById("client-label-print");
      if (!el) return;
      const win = window.open("", "_blank", "width=400,height=350");
      if (!win) return;
      // Escape user-controlled values used in <title> to prevent XSS via crafted client names
      const safeName = String(client.name || "")
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
      win.document.write(`<html><head><title>Etiqueta - ${safeName}</title><style>body{margin:0;padding:10px;font-family:Arial,sans-serif}@media print{body{padding:0}}.label{width:2.8in;height:2in;border:1px dashed #ccc;padding:10px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center}.logo{font-size:14px;font-weight:bold;margin-bottom:4px}.name{font-size:12px;margin:4px 0}.code{font-size:16px;font-weight:bold;font-family:monospace;letter-spacing:2px;margin-top:4px}</style></head><body>${el.innerHTML}</body></html>`);
      win.document.close();
      win.focus();
      win.print();
    }, 300);
  };

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/admin/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(res.data.clients);
      setTotal(res.data.total);
    } catch (error) {
      toast.error("Error al cargar clientes");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingClient(null);
    setForm({ name: "", email: "", phone: "", street_address: "", unit: "", city: "", state: "", zip_code: "", service_types: [], service_date: "", notes: "" });
    setIsFormOpen(true);
  };

  const handleOpenEdit = (client) => {
    setEditingClient(client);
    setForm({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      street_address: client.street_address || "",
      unit: client.unit || "",
      city: client.city || "",
      state: client.state || "",
      zip_code: client.zip_code || "",
      service_types: client.service_types || (client.service_type ? [client.service_type] : []),
      service_date: client.service_date || "",
      notes: client.notes || ""
    });
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Nombre y email son obligatorios");
      return;
    }
    setIsSaving(true);
    const payload = { ...form, city_state: [form.city, form.state].filter(Boolean).join(", ") };
    try {
      if (editingClient) {
        await axios.put(`${API}/admin/clients/${editingClient.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Cliente actualizado");
      } else {
        await axios.post(`${API}/admin/clients`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Cliente registrado");
      }
      setIsFormOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("¿Eliminar este cliente?")) return;
    try {
      await axios.delete(`${API}/admin/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cliente eliminado");
      fetchClients();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Código ${code} copiado`);
  };

  const handleApprove = async (clientId) => {
    try {
      await axios.put(`${API}/admin/clients/${clientId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Cliente aprobado. Correo enviado con código.");
      fetchClients();
    } catch (e) { toast.error("Error al aprobar"); }
  };

  const toggleFormService = (service) => {
    setForm(prev => ({
      ...prev,
      service_types: prev.service_types.includes(service)
        ? prev.service_types.filter(s => s !== service)
        : [...prev.service_types, service]
    }));
  };

  const copyRegisterLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/register`);
    toast.success("Enlace de registro copiado");
  };

  const filtered = clients.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city_state?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-stone-50" data-testid="clients-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="clients-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Admin
            </Button>
            <div className="flex items-center gap-2">
              <img src="/assets/logo-sally.png" alt="Sally" className="h-8" onError={(e) => e.target.style.display = 'none'} />
              <div>
                <h1 className="text-lg font-bold flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  Gestión de Clientes
                </h1>
                <p className="text-xs text-stone-500">{total} clientes registrados</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copyRegisterLink} data-testid="copy-register-link">
              <Link className="w-4 h-4 mr-2" /> Enlace de Registro
            </Button>
            <Button onClick={handleOpenCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-client-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Cliente
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Buscar por nombre, email, código o ciudad..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white"
            data-testid="clients-search-input"
          />
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-stone-500">Cargando clientes...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 mx-auto mb-4 text-stone-300" />
            <p className="text-stone-500 text-lg mb-2">
              {searchTerm ? "No se encontraron clientes" : "No hay clientes registrados"}
            </p>
            {!searchTerm && (
              <Button onClick={handleOpenCreate} variant="outline">
                <Plus className="w-4 h-4 mr-2" /> Registrar primer cliente
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((client) => (
              <Card
                key={client.id}
                className="hover:shadow-md transition-shadow border-stone-200"
                data-testid={`client-card-${client.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-stone-900">{client.name}</h3>
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        <Badge
                          className="bg-emerald-100 text-emerald-700 font-mono text-sm cursor-pointer hover:bg-emerald-200"
                          onClick={() => copyCode(client.code)}
                          data-testid={`client-code-${client.code}`}
                        >
                          <Hash className="w-3 h-3 mr-1" />
                          {client.code}
                          <Copy className="w-3 h-3 ml-1 opacity-50" />
                        </Badge>
                        {client.is_approved === false && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3 mr-1" /> Pendiente
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {client.is_approved === false && (
                        <Button size="sm" variant="ghost" className="text-emerald-600 hover:bg-emerald-50" onClick={() => handleApprove(client.id)} title="Aprobar" data-testid={`client-approve-${client.id}`}>
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setSelectedClient(client)} data-testid={`client-view-${client.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openClientOrders(client)} title="Ver pedidos del cliente" data-testid={`client-orders-${client.id}`}>
                        <Package className="w-4 h-4 text-indigo-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handlePrintLabel(client)} title="Imprimir Etiqueta" data-testid={`client-label-${client.id}`}>
                        <Printer className="w-4 h-4 text-purple-500" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(client)} data-testid={`client-edit-${client.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(client.id)} data-testid={`client-delete-${client.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-1.5 text-sm text-stone-600">
                    <div className="flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5 text-stone-400" />
                      <span className="truncate">{client.email}</span>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-stone-400" />
                        <span>{client.phone}</span>
                      </div>
                    )}
                    {(client.city_state || client.city || client.street_address) && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                        <span className="text-xs">{client.street_address ? `${client.street_address}${client.unit ? ` ${client.unit}` : ''}, ` : ''}{client.city_state || [client.city, client.state, client.zip_code].filter(Boolean).join(", ")}</span>
                      </div>
                    )}
                    {(client.service_types?.length > 0 || client.service_type) && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <FileText className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                        {(client.service_types || [client.service_type]).filter(Boolean).map(s => (
                          <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                        ))}
                      </div>
                    )}
                    {client.service_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-stone-400" />
                        <span className="text-xs">{new Date(client.service_date).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              {editingClient ? "Editar Cliente" : "Nuevo Cliente"}
            </DialogTitle>
            <DialogDescription>
              {editingClient ? "Modifica los datos del cliente" : "Registra un nuevo cliente. Se generará un código único automáticamente."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Nombre completo" data-testid="client-form-name" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="email@ejemplo.com" data-testid="client-form-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 234 567 8900" data-testid="client-form-phone" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Dirección de la Calle</Label>
              <Input value={form.street_address} onChange={(e) => setForm(p => ({ ...p, street_address: e.target.value }))} placeholder="123 Main Street" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unidad / Apto / Suite</Label>
                <Input value={form.unit} onChange={(e) => setForm(p => ({ ...p, unit: e.target.value }))} placeholder="Apt 4B, Suite 200..." />
              </div>
              <div className="space-y-2">
                <Label>Ciudad</Label>
                <Input value={form.city} onChange={(e) => setForm(p => ({ ...p, city: e.target.value }))} placeholder="Miami" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={form.state} onValueChange={(v) => setForm(p => ({ ...p, state: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent className="max-h-60">
                    {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Código Postal</Label>
                <Input value={form.zip_code} onChange={(e) => setForm(p => ({ ...p, zip_code: e.target.value }))} placeholder="33101" maxLength={10} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label>Servicios</Label>
                <div className="grid grid-cols-2 gap-2">
                  {serviceTypes.map(service => (
                    <label key={service} className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${form.service_types.includes(service) ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-stone-300'}`}>
                      <Checkbox checked={form.service_types.includes(service)} onCheckedChange={() => toggleFormService(service)} />
                      <span className="text-sm">{service}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha del Trabajo</Label>
                <Input type="date" value={form.service_date} onChange={(e) => setForm(p => ({ ...p, service_date: e.target.value }))} data-testid="client-form-date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notas internas sobre el cliente..." rows={3} data-testid="client-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={isSaving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="client-form-save-btn">
              {isSaving ? "Guardando..." : (editingClient ? "Guardar Cambios" : "Registrar Cliente")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedClient} onOpenChange={(o) => !o && setSelectedClient(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Detalle del Cliente</DialogTitle>
          </DialogHeader>
          {selectedClient && (
            <div className="space-y-4" data-testid="client-detail-dialog">
              <div className="text-center pb-2">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl font-bold text-emerald-700">{selectedClient.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <h3 className="text-xl font-bold">{selectedClient.name}</h3>
                <Badge className="bg-emerald-100 text-emerald-700 font-mono text-base mt-2 cursor-pointer" onClick={() => copyCode(selectedClient.code)}>
                  <Hash className="w-4 h-4 mr-1" /> {selectedClient.code} <Copy className="w-3 h-3 ml-2 opacity-50" />
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Email</p>
                  <p className="font-medium text-sm">{selectedClient.email}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Teléfono</p>
                  <p className="font-medium text-sm">{selectedClient.phone || "-"}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg col-span-2">
                  <p className="text-xs text-stone-500 mb-1">Dirección</p>
                  <p className="font-medium text-sm">
                    {selectedClient.street_address ? `${selectedClient.street_address}${selectedClient.unit ? `, ${selectedClient.unit}` : ''}` : '-'}
                  </p>
                  <p className="text-sm text-stone-600">
                    {[selectedClient.city, selectedClient.state, selectedClient.zip_code].filter(Boolean).join(", ") || selectedClient.city_state || ""}
                  </p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Servicios</p>
                  <div className="flex flex-wrap gap-1">
                    {(selectedClient.service_types || [selectedClient.service_type]).filter(Boolean).map(s => (
                      <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
                    )) || <span className="text-sm">-</span>}
                  </div>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Fecha del Trabajo</p>
                  <p className="font-medium text-sm">{selectedClient.service_date ? new Date(selectedClient.service_date).toLocaleDateString() : "-"}</p>
                </div>
                <div className="p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Registrado</p>
                  <p className="font-medium text-sm">{new Date(selectedClient.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              {selectedClient.notes && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Notas</p>
                  <p className="text-sm">{selectedClient.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hidden Label for printing */}
      {labelClient && (
        <div style={{ position: "fixed", left: "-9999px", top: 0 }}>
          <div id="client-label-print">
            <div className="label">
              <div className="logo">SALLY BETTER</div>
              <div style={{ fontSize: 9, color: "#888" }}>Print & Digital Solutions</div>
              <QRCodeSVG
                value={`${window.location.origin}/encuesta?code=${labelClient.code}`}
                size={90}
                level="M"
                style={{ margin: "6px 0" }}
              />
              <div className="name">{labelClient.name}</div>
              <div className="code">{labelClient.code}</div>
              <div style={{ fontSize: 8, color: "#999", marginTop: 3 }}>Escanea para completar tu encuesta</div>
            </div>
          </div>
        </div>
      )}

      {/* Client Orders Dialog */}
      <Dialog open={!!ordersClient} onOpenChange={(o) => !o && setOrdersClient(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="client-orders-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" />
              Pedidos de {ordersClient?.name}
            </DialogTitle>
            <DialogDescription>
              Código <span className="font-mono font-semibold text-emerald-700">{ordersClient?.code}</span> · {clientOrders.length} pedido(s) en total
            </DialogDescription>
          </DialogHeader>
          {ordersLoading ? (
            <div className="py-12 text-center text-stone-400">Cargando...</div>
          ) : clientOrders.length === 0 ? (
            <div className="py-12 text-center text-stone-400" data-testid="client-orders-empty">
              <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>Este cliente aún no tiene pedidos registrados.</p>
            </div>
          ) : (() => {
            const active = clientOrders.filter(o => ACTIVE_STATUSES.includes(o.status));
            const completed = clientOrders.filter(o => !ACTIVE_STATUSES.includes(o.status));

            const renderOrder = (order) => {
              const cfg = ORDER_STATUS_CONFIG[order.status] || ORDER_STATUS_CONFIG.received;
              const Icon = cfg.icon;
              return (
                <div key={order.id} className="rounded-xl border bg-white p-3 hover:border-indigo-200 transition-colors" data-testid={`client-order-row-${order.id}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-indigo-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-mono font-semibold text-indigo-600 text-sm truncate">{order.tracking_number}</p>
                        <p className="text-xs text-stone-500 truncate">
                          {(order.items || []).map(i => i.description).filter(Boolean).join(", ") || order.description || "—"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge className={cfg.color}>{cfg.label}</Badge>
                      <span className="text-xs text-stone-400 hidden sm:inline">
                        {order.created_at ? new Date(order.created_at).toLocaleDateString() : ""}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-indigo-600"
                        onClick={() => window.open(`/tracking/${order.tracking_number}`, "_blank")}
                        title="Ver tracking público"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            };

            return (
              <div className="space-y-5 py-2">
                <div>
                  <h3 className="text-sm font-semibold text-stone-800 mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" /> En Proceso
                    <Badge className="bg-amber-100 text-amber-700">{active.length}</Badge>
                  </h3>
                  {active.length === 0 ? (
                    <p className="text-xs text-stone-400 px-1">Sin pedidos activos.</p>
                  ) : (
                    <div className="space-y-2" data-testid="client-orders-active">
                      {active.map(renderOrder)}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-stone-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-blue-600" /> Completados
                    <Badge className="bg-blue-100 text-blue-700">{completed.length}</Badge>
                  </h3>
                  {completed.length === 0 ? (
                    <p className="text-xs text-stone-400 px-1">Aún no hay pedidos completados.</p>
                  ) : (
                    <div className="space-y-2" data-testid="client-orders-completed">
                      {completed.map(renderOrder)}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrdersClient(null)}>Cerrar</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => { setOrdersClient(null); navigate("/admin/orders"); }}
              data-testid="client-orders-open-orders-page"
            >
              Ir al panel de pedidos <Box className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsPage;
